"use strict";
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.registerField = void 0;
const react_1 = require("react");
const Field_1 = require("./Field");
const HoneyForm_1 = require("./HoneyForm");
/**
 * Registers a field and refreshes callee on updates
 *
 * Only one component that manages the field should register it, other components using
 * that field should use the {@link useField} hook instead
 *
 * @param fieldName Unique name (slug) to use for this field
 * @param options Field configuration including default value and validation settings
 * @param watchUpdates Whether we want to update callee on form updates
 *
 * @returns {Field} A new field
 */
const registerField = (fieldName, options = {}, watchUpdates = true) => {
    const { defaultValue = "", fieldRef } = options, validationOptions = __rest(options, ["defaultValue", "fieldRef"]);
    const form = (0, HoneyForm_1.useForm)(false);
    // there's a chance the field was removed and re-registered, but we want to maintain the
    // form's initialValue over the field's defaultValue
    const getDefaultValue = () => {
        const initialValue = form.getInitialValue(fieldName);
        return initialValue !== undefined ? initialValue : defaultValue;
    };
    // add new fields
    if (!form.hasField(fieldName)) {
        form.addField(new Field_1.Field(fieldName, getDefaultValue(), form));
    }
    // fetch field from form
    const field = (0, HoneyForm_1.useField)(fieldName, watchUpdates);
    // configure once, remove field on unmount
    (0, react_1.useEffect)(() => {
        // only need to set validation options once
        field.configureValidation(validationOptions);
        // add ref to field
        if (fieldRef !== undefined) {
            field.setRef(fieldRef);
        }
        // we eagerly add new fields to the form (above) so they are available
        // on first render before the Effect triggers, however, StrictMode will
        // do an Effect setup -> cleanup -> setup and remove our field
        // this keeps everyone happy even if Effect fires twice
        if (!form.hasField(fieldName)) {
            form.addField(field);
        }
        // cleanup on unmount
        return () => {
            field.reset(); // remove errors, let dependencies know field is cleared
            // field might have been removed elsewhere, and we don't need cleanup
            // this can also happen in StrictMode if there are two fields sharing a name
            // (which isn't ideal, but doesn't need to error)
            if (form.hasField(fieldName)) {
                form.removeField(fieldName);
            }
        };
    }, []);
    return field;
};
exports.registerField = registerField;
